<script setup lang="ts">
import { tpgIconCircleFilledGreenCustom } from '@tpg/grammar-web-icons';
import type { MaybeElementRef } from '@vueuse/core';
import { onClickOutside } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { computed, ref } from 'vue';

import {
  GrammarOverlayCard,
  GrammarRadioButtonGroup,
  GrammarRadioButtonItem,
  GrammarSectionHeader,
} from '@nora-monorepo/grammar-ui';

import {
  DrawToolColor,
  FillStyle,
  isDrawToolColor,
  isFillStyle,
} from '#nora-ui/map/map/DrawToolUIManager';
import { useMapStore } from '#nora-ui/map/stores/map-store';

export interface EditingFillTabProps {
  ignoredClickOutsideElements?: MaybeRefOrGetter<(MaybeElementRef | string)[]>;
  mapId: string;
}
const props = withDefaults(defineProps<EditingFillTabProps>(), {
  ignoredClickOutsideElements: () => [],
});

const emit = defineEmits(['close']);
const containerRef = ref(null);
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const drawToolUI = map.drawToolUI;
const fillColor = drawToolUI.fillColor;
const fillStyle = drawToolUI.fillStyle;
const fillOpacity = drawToolUI.fillOpacity;

function onColorChange(hexColor?: string) {
  if (hexColor && isDrawToolColor(hexColor)) {
    fillColor.value = hexColor;
  }
}

function onStyleChange(value?: string) {
  if (value && isFillStyle(value)) {
    fillStyle.value = value;
  }
}

function onOpacityChange(value?: string) {
  if (!value) {
    return;
  }

  const opacity = Number.parseFloat(value);
  if (Number.isFinite(opacity)) {
    fillOpacity.value = opacity;
  }
}

const showFillStyle = computed(() => {
  return drawToolUI.getActiveDrawTool()?.showFillStyle();
});

onClickOutside(
  containerRef,
  () => {
    emit('close');
  },
  {
    ignore: props.ignoredClickOutsideElements,
  },
);

// TODO: Grammar 1.1 change: Need to add colors for icons.
</script>

<template>
  <div ref="containerRef">
    <GrammarOverlayCard
      label=""
      class="pointer-events-auto max-w-[320px] medium:max-w-auto"
      style="
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftIconOnlyEnabledWidth: 20px;
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftEnabledMinWidth: 20px;
      "
    >
      <div class="p-1">
        <GrammarSectionHeader
          label="Fill Color"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="fillColor"
          @input="onColorChange"
        >
          <GrammarRadioButtonItem
            v-for="color in Object.values(DrawToolColor)"
            :key="color"
            :icon-template="tpgIconCircleFilledGreenCustom"
            :icon-color="color"
            :value="color"
          />
        </GrammarRadioButtonGroup>
      </div>
      <div
        v-if="showFillStyle"
        class="p-1"
      >
        <GrammarSectionHeader
          label="Style"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="fillStyle"
          @input="onStyleChange"
        >
          <GrammarRadioButtonItem
            v-for="style in [FillStyle.NoFill, FillStyle.Solid]"
            :key="style"
            :label="style === FillStyle.NoFill ? 'No fill' : 'Solid fill'"
            :value="style"
          />
        </GrammarRadioButtonGroup>
      </div>
      <div class="p-1">
        <GrammarSectionHeader
          label="Opacity"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="`${fillOpacity}`"
          @input="onOpacityChange"
        >
          <GrammarRadioButtonItem
            label="25%"
            value="0.25"
          />
          <GrammarRadioButtonItem
            label="50%"
            value="0.5"
          />
          <GrammarRadioButtonItem
            label="75%"
            value="0.75"
          />
          <GrammarRadioButtonItem
            label="100%"
            value="1"
          />
        </GrammarRadioButtonGroup>
      </div>
    </GrammarOverlayCard>
  </div>
</template>
